import React, { useState, useEffect, useRef } from "react";
import { Input, InputLabel } from "../../components/Products/Input";
import { useNavigate, useParams } from "react-router-dom";
import Image from "../../components/Image";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { fetchCategories } from "../../utils/dataFetcher";
import MultiSelectDropdown from "../../components/MultiSelectDropdown/MultiSelectDropdown";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;
export const Arrow = () => {
  const { i18n } = useTranslation("supplier");
  const lang = i18n.language;

  return (
    <svg
      className={`absolute ${
        lang === "ar" ? "left-[12px]" : "right-[12px]"
      } top-[12px]`}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 16.8C11.3 16.8 10.6 16.53 10.07 16L3.55002 9.48001C3.26002 9.19001 3.26002 8.71001 3.55002 8.42001C3.84002 8.13001 4.32002 8.13001 4.61002 8.42001L11.13 14.94C11.61 15.42 12.39 15.42 12.87 14.94L19.39 8.42001C19.68 8.13001 20.16 8.13001 20.45 8.42001C20.74 8.71001 20.74 9.19001 20.45 9.48001L13.93 16C13.4 16.53 12.7 16.8 12 16.8Z"
        fill="#292D32"
      />
    </svg>
  );
};

const InputForm = ({
  formData,
  setFormData,
  handleSubmit,
  error,
  imageFocus = {},
  setImageFocus,
}) => {
  console.log("InputForm rendered", formData);
  const { t, i18n } = useTranslation("supplier");
  const navigate = useNavigate();
  const [valid, setValid] = useState(false);
  const { id } = useParams();
  const { isAuthenticated } = useAuth();
  const [categories, setCategories] = useState([]);

  // Portfolio drag & drop states
  const [isPortfolioDragging, setIsPortfolioDragging] = useState(false);

  // Image validation state
  const [imageError, setImageError] = useState("");

  // Focus-related states
  const [focusOverlay, setFocusOverlay] = useState({
    show: false,
    image: null,
    type: "preview",
  });
  const [focusPoint, setFocusPoint] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const overlayRef = useRef(null);

  // Target dimensions for overlay display
  const TARGET_WIDTH = 1700;
  const TARGET_HEIGHT = 450;
  const TARGET_ASPECT_RATIO = TARGET_WIDTH / TARGET_HEIGHT;
  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };
  useEffect(() => {
    fetchCategories(getLangFromLocalStorage())
      .then((data) => {
        const categoriesData = data.data || data;
        console.log("Fetched categories:", categoriesData);
        setCategories(categoriesData);
      })
      .catch((err) => console.error("Error fetching categories:", err));
  }, []);

  // Debug effect to check service type values
  useEffect(() => {
    if (formData.service_type && categories.length > 0) {
      console.log("InputForm service_type:", formData.service_type);
      console.log("InputForm categories:", categories);
      console.log(
        "InputForm selected key:",
        getServiceTypeKey(formData.service_type)
      );
    }
  }, [formData.service_type, categories]);

  // Debug effect specifically for edit mode
  useEffect(() => {
    if (id && formData.service_type) {
      console.log(
        "EDIT MODE - Original service_type from API:",
        formData.service_type
      );
      console.log("EDIT MODE - Type:", typeof formData.service_type);
      console.log("EDIT MODE - Categories available:", categories.length);
      if (categories.length > 0) {
        console.log(
          "EDIT MODE - Processed key:",
          getServiceTypeKey(formData.service_type)
        );
      }
    }
  }, [id, formData.service_type, categories]);

  // Add global event listeners for mouse events when dragging
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = (e) => handleMouseUp(e);
      const handleGlobalTouchMove = (e) => handleTouchMove(e);
      const handleGlobalTouchEnd = (e) => handleTouchEnd(e);

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchmove", handleGlobalTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleGlobalTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.removeEventListener("touchmove", handleGlobalTouchMove);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };
    }
  }, [isDragging, focusPoint]);
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type.startsWith("image/")) {
      setFormData({
        ...formData,
        ["image"]: selectedFile,
        ["preview"]: URL.createObjectURL(selectedFile),
      });
      setImageError(""); // Clear image error when file is selected
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // Handle file inputs
    if (files && files[0]) {
      setFormData({
        ...formData,
        [name]: files[0],
      });
      return;
    }

    if (name === "service_types") {
      const selectedServiceTypeIds = Array.from(
        e.target.selectedOptions,
        (option) => parseInt(option.value, 10)
      ).filter((id) => Number.isInteger(id) && id > 0);
      setFormData({
        ...formData,
        service_types: selectedServiceTypeIds,
        service_type: selectedServiceTypeIds[0] || "",
      });
    } else if (name === "cities") {
      const selectedCityIds = Array.from(
        e.target.selectedOptions,
        (option) => parseInt(option.value, 10)
      ).filter((id) => Number.isInteger(id) && id > 0);
      const nextShippingPrices = {};
      selectedCityIds.forEach((cityId) => {
        nextShippingPrices[cityId] =
          formData.shipping_prices?.[cityId] ??
          formData.shipping_prices?.[String(cityId)] ??
          "";
      });
      setFormData({
        ...formData,
        cities: selectedCityIds,
        city: selectedCityIds[0] || "",
        shipping_prices: nextShippingPrices,
      });
    } else if (name === "service_type") {
      setFormData({
        ...formData,
        service_type: value,
        service_types: normalizeIdList(value),
      });
    } else if (name === "city") {
      const selectedCityIds = normalizeIdList(value);
      const nextShippingPrices = {};
      selectedCityIds.forEach((cityId) => {
        nextShippingPrices[cityId] =
          formData.shipping_prices?.[cityId] ??
          formData.shipping_prices?.[String(cityId)] ??
          "";
      });
      setFormData({
        ...formData,
        city: value,
        cities: selectedCityIds,
        shipping_prices: nextShippingPrices,
      });
    } else {
      // For other fields, make sure service_type remains as ID if it was already set
      const updatedFormData = { ...formData, [name]: value };

      // If service_type exists and is not already an ID, convert it
      if (updatedFormData.service_type && categories.length > 0) {
        const serviceTypeId = getServiceTypeKey(updatedFormData.service_type);
        if (serviceTypeId && serviceTypeId !== updatedFormData.service_type) {
          updatedFormData.service_type = parseInt(serviceTypeId, 10);
        }
      }

      setFormData(updatedFormData);
    }
  };

  // Portfolio drag & drop handlers
  const handlePortfolioDragOver = (e) => {
    e.preventDefault();
    setIsPortfolioDragging(true);
  };

  const handlePortfolioDragLeave = (e) => {
    e.preventDefault();
    setIsPortfolioDragging(false);
  };

  const handlePortfolioDrop = (e) => {
    e.preventDefault();
    setIsPortfolioDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFormData({
        ...formData,
        portfolio: droppedFile,
      });
    }
  };

  const handlePortfolioBrowseClick = () => {
    document.getElementById("portfolioInput").click();
  };

  useEffect(() => {
    const selectedCityIds = getSelectedCityIds();
    const allCityShippingPricesValid =
      selectedCityIds.length > 0 &&
      selectedCityIds.every((cityId) => {
        const price =
          formData.shipping_prices?.[cityId] ??
          formData.shipping_prices?.[String(cityId)];
        return (
          price !== "" &&
          price != null &&
          !isNaN(Number(price)) &&
          Number(price) >= 0
        );
      });

    setValid(
      formData.name &&
        getSelectedServiceTypeIds().length > 0 &&
        formData.country &&
        selectedCityIds.length > 0 &&
        /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(
          formData.supported_email
        ) &&
        formData.preview &&
        formData.about?.length >= 10 &&
        formData.terms_and_conditions?.length >= 10 &&
        allCityShippingPricesValid
    );
  }, [formData, categories]); // Add categories to dependency array

  const getServiceTypeKey = (value) => {
    console.log("getServiceTypeKey called with:", value, "type:", typeof value);
    console.log("Available categories:", categories);

    if (!value || categories.length === 0) return "";

    // Handle different data formats

    // If value is already a number (ID), verify it exists in categories
    if (typeof value === "number") {
      const category = categories.find((cat) => cat.id === value);
      const result = category ? String(value) : "";
      console.log("Number input result:", result);
      return result;
    }

    // If value is a string, could be either ID or name
    if (typeof value === "string") {
      const trimmedValue = value.trim();

      // First try to parse as number (ID)
      const numericValue = parseInt(trimmedValue, 10);
      if (!isNaN(numericValue) && numericValue > 0) {
        const category = categories.find((cat) => cat.id === numericValue);
        if (category) {
          console.log("String-to-number input result:", String(numericValue));
          return String(numericValue);
        }
      }

      // Then try to find by exact name match
      const categoryByName = categories.find(
        (cat) =>
          cat.name && cat.name.toLowerCase() === trimmedValue.toLowerCase()
      );
      if (categoryByName) {
        console.log("String name input result:", String(categoryByName.id));
        return String(categoryByName.id);
      }

      // Finally try exact string match for ID
      const categoryByStringId = categories.find(
        (cat) => String(cat.id) === trimmedValue
      );
      if (categoryByStringId) {
        console.log("String ID input result:", trimmedValue);
        return trimmedValue;
      }
    }

    // Handle category objects (in case API returns full category object)
    if (value && typeof value === "object") {
      if (value.id) {
        const category = categories.find((cat) => cat.id === value.id);
        const result = category ? String(value.id) : "";
        console.log("Object input result:", result);
        return result;
      }
      if (value.name) {
        const category = categories.find(
          (cat) =>
            cat.name && cat.name.toLowerCase() === value.name.toLowerCase()
        );
        const result = category ? String(category.id) : "";
        console.log("Object name input result:", result);
        return result;
      }
    }

    console.log("No match found, returning empty string");
    return "";
  };

  // Focus-related functions
  const openFocusSelector = () => {
    const currentFocus = imageFocus["supplier_image"] || { x: 50, y: 50 };
    setFocusPoint(currentFocus);
    setFocusOverlay({ show: true, image: formData.preview, type: "preview" });
  };

  const closeFocusSelector = () => {
    setFocusOverlay({ show: false, image: null, type: "preview" });
    setIsDragging(false);
  };

  const getPositionFromEvent = (event) => {
    if (!overlayRef.current) return null;

    const rect = overlayRef.current.getBoundingClientRect();
    let clientX, clientY;

    if (event.type.includes("touch")) {
      clientX = event.touches[0]?.clientX || event.changedTouches[0]?.clientX;
      clientY = event.touches[0]?.clientY || event.changedTouches[0]?.clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }

    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    return {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    };
  };

  const handleMouseDown = (event) => {
    event.preventDefault();
    setIsDragging(true);
    const position = getPositionFromEvent(event);
    if (position) {
      setFocusPoint(position);
    }
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;
    event.preventDefault();
    const position = getPositionFromEvent(event);
    if (position) {
      setFocusPoint(position);
    }
  };

  const handleMouseUp = (event) => {
    if (isDragging) {
      event.preventDefault();
      setIsDragging(false);
    }
  };

  const handleTouchStart = (event) => {
    event.preventDefault();
    setIsDragging(true);
    const position = getPositionFromEvent(event);
    if (position) {
      setFocusPoint(position);
    }
  };

  const handleTouchMove = (event) => {
    if (!isDragging) return;
    event.preventDefault();
    const position = getPositionFromEvent(event);
    if (position) {
      setFocusPoint(position);
    }
  };

  const handleTouchEnd = (event) => {
    if (isDragging) {
      event.preventDefault();
      setIsDragging(false);
    }
  };

  const saveFocusPoint = () => {
    if (!setImageFocus) return;

    setImageFocus({
      ...imageFocus,
      ["supplier_image"]: focusPoint,
    });
    closeFocusSelector();
  };

  const getImageStyle = () => {
    const focus = imageFocus["supplier_image"];
    if (!focus) return {};

    return {
      objectPosition: `${focus.x}% ${focus.y}%`,
    };
  };

  const getFocusPreview = () => {
    const focus = imageFocus["supplier_image"];
    if (!focus) return null;

    return (
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute w-2 h-2 bg-blue-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 border-2 border-white shadow-lg"
          style={{
            left: `${focus.x}%`,
            top: `${focus.y}%`,
          }}
        />
      </div>
    );
  };

  const countryOptions = [{ id: 1, name: "Saudi Arabia", name_ar: "المملكة العربية السعودية" }];
  const [cities, setCities] = useState([]);

  const normalizeIdList = (value) => {
    if (value == null) return [];
    const values = Array.isArray(value) ? value : String(value).split(",");
    return values
      .map((item) => parseInt(String(item).trim(), 10))
      .filter((id) => Number.isInteger(id) && id > 0);
  };

  const getSelectedServiceTypeIds = () => {
    const fromArray = normalizeIdList(formData.service_types);
    if (fromArray.length > 0) return fromArray;
    const fromLegacy = getServiceTypeKey(formData.service_type);
    return fromLegacy ? [parseInt(fromLegacy, 10)] : [];
  };

  const getSelectedCityIds = () => {
    const fromArray = normalizeIdList(formData.cities);
    if (fromArray.length > 0) return fromArray;
    return normalizeIdList(formData.city);
  };

  const handleServiceTypesChange = (selectedServiceTypeIds) => {
    setFormData({
      ...formData,
      service_types: selectedServiceTypeIds,
      service_type: selectedServiceTypeIds[0] || "",
    });
  };

  const handleCitiesChange = (selectedCityIds) => {
    const nextShippingPrices = {};
    selectedCityIds.forEach((cityId) => {
      nextShippingPrices[cityId] =
        formData.shipping_prices?.[cityId] ??
        formData.shipping_prices?.[String(cityId)] ??
        "";
    });
    setFormData({
      ...formData,
      cities: selectedCityIds,
      city: selectedCityIds[0] || "",
      shipping_prices: nextShippingPrices,
    });
  };

  const handleCityShippingPriceChange = (cityId, priceValue) => {
    setFormData({
      ...formData,
      shipping_prices: {
        ...(formData.shipping_prices || {}),
        [cityId]: priceValue,
      },
    });
  };

  const getCountryIdFromName = (countryName) => {
    const country = countryOptions.find((opt) => opt.name === countryName || opt.name_ar === countryName);
    return country ? country.id : "";
  };

  // Helper function to get country value for select
  const getCountryValue = () => {
    // If country is already an ID (number), use it directly
    if (
      typeof formData.country === "number" ||
      !isNaN(Number(formData.country))
    ) {
      return formData.country;
    }
    // If country is a name (string), convert to ID
    if (typeof formData.country === "string") {
      return getCountryIdFromName(formData.country);
    }
    return "";
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    fetch(`${baseUrl}/api/v1/cities`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        lang: getLangFromLocalStorage(),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const citiesData = data.data || data;
        setCities(citiesData);

        if (
          id &&
          !formData.cities &&
          formData.city &&
          typeof formData.city === "string"
        ) {
          const foundCity = citiesData.find(
            (city) => city.name === formData.city
          );
          if (foundCity) {
            setFormData((prev) => {
              const existingPrice =
                prev.shipping_prices?.[foundCity.id] ??
                prev.shipping_prices?.[String(foundCity.id)] ??
                "";
              return {
                ...prev,
                city: foundCity.id,
                cities: [foundCity.id],
                shipping_prices: {
                  [foundCity.id]: existingPrice,
                },
              };
            });
          }
        }
      })
      .catch((err) => console.error("Error fetching cities:", err));
  }, [id]);
  // Convert service_type to ID when categories are loaded or formData changes
  useEffect(() => {
    if (formData.service_type && !formData.service_types && categories.length > 0) {
      const serviceTypeId = getServiceTypeKey(formData.service_type);

      // Only update if current service_type is not already the correct ID
      if (serviceTypeId && String(formData.service_type) !== serviceTypeId) {
        console.log(
          "Converting service_type from",
          formData.service_type,
          "to ID",
          serviceTypeId
        );
        setFormData((prev) => ({
          ...prev,
          service_type: parseInt(serviceTypeId, 10),
          service_types: [parseInt(serviceTypeId, 10)],
        }));
      }
    }
  }, [categories, formData.service_type, formData.service_types]);

  // Convert country name to ID when form data is first loaded
  useEffect(() => {
    if (
      formData.country &&
      typeof formData.country === "string" &&
      formData.country !== ""
    ) {
      const countryId = getCountryIdFromName(formData.country);
      if (countryId && countryId !== formData.country) {
        setFormData((prev) => ({ ...prev, country: countryId }));
      }
    }
  }, [formData.country]);
  return (
    <div className={"flex flex-col py-[56px]"}>
      <div className={"w-[220px] flex flex-row gap-[8px]"}>
        {formData.preview ? (
          <div className="bg-[#f5f5f5] border-[1px] border-[#dbdbdb] relative w-[172px] h-[132px] overflow-hidden rounded-[4px] mb-[16px] group">
            <Image
              Class={
                "object-cover w-[172px] h-[132px] transition-transform duration-200"
              }
              src={formData.preview}
              alt={t("alt.remove")}
              style={getImageStyle()}
            />
            {getFocusPreview()}

            <div className="absolute top-2 right-2 flex gap-1">
              <button
                type="button"
                className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onClick={openFocusSelector}
                title={t("focus.setFocus")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="12"
                    y1="2"
                    x2="12"
                    y2="6"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="12"
                    y1="18"
                    x2="12"
                    y2="22"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="2"
                    y1="12"
                    x2="6"
                    y2="12"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="18"
                    y1="12"
                    x2="22"
                    y2="12"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onClick={() => {
                  setFormData({
                    ...formData,
                    ["preview"]: null,
                    ["image"]: null,
                  });
                  setImageError(""); // Clear error when image is removed
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="upload-image"
            />
            <label
              htmlFor="upload-image"
              className="bg-[#f5f5f5] border-[1px] border-[#dbdbdb] cursor-pointer rounded-[4px] flex justify-center items-center w-[172px] h-[132px]"
            >
              <img src="/gallery-add.svg" alt={t("alt.uploadIcon")} />
            </label>
          </div>
        )}
        <span className="text-red-500 text-3xl mt-[-20px]">
          {t("asterisk")}
        </span>
      </div>
      <p className="text-[12px] text-gray-500 mt-2 ">
        {t("supplierDetails.imageRequirements")}
      </p>
      {imageError && (
        <p className="text-red-500 text-[12px] mt-1">
          {imageError}
        </p>
      )}
      <form
        className="form-container"
        onSubmit={(e) => {
          e.preventDefault();
          if (!formData.preview) {
            setImageError(t("validation.imageRequired"));
            return;
          }
          setImageError(""); // Clear error if image exists
          if (!valid) return;

          // Submit multi-select relations and keep legacy scalar fallback
          const submissionData = { ...formData };
          submissionData.service_types = getSelectedServiceTypeIds();
          submissionData.cities = getSelectedCityIds();
          submissionData.service_type = submissionData.service_types[0] || "";
          submissionData.city = submissionData.cities[0] || "";

          console.log("Form submission data:", submissionData);
          console.log(
            "Service type being submitted:",
            submissionData.service_type,
            typeof submissionData.service_type
          );

          // Update formData to ensure it maintains the ID format
          setFormData(submissionData);

          handleSubmit(e, submissionData);
        }}
      >
        <div className="flex-1">
          {/* Supplier Name */}
          <div className="mb-4 relative">
            <InputLabel>{t("labels.supplierName")}</InputLabel>
            <Input
              type="text"
              name="name"
              lang={i18n.language}
              value={formData.name}
              onChange={handleChange}
              placeholder={t("placeholders.supplierName")}
              required
            />
            {error.errors?.name && (
              <p className="absolute top-20 text-red-500 text-[12px] mt-1">
                {error.errors.name}
              </p>
            )}
          </div>

          {/* Service Type */}
          <div className="mb-4 relative">
            <InputLabel>{t("labels.serviceType")}</InputLabel>
            <div className={"relative"}>
              <MultiSelectDropdown
                options={categories.map((category) => ({
                  value: category.id,
                  label: category.name,
                }))}
                value={getSelectedServiceTypeIds()}
                onChange={handleServiceTypesChange}
                placeholder={t("labels.selectServiceType")}
              />
            </div>
            {error.errors?.[0]?.service_type && (
              <p className="absolute top-20 text-red-500 text-[12px] mt-1">
                {error.errors[0].service_type}.
              </p>
            )}
          </div>

          {/* Country */}
          <div className="mb-4 relative">
            <InputLabel>{t("labels.country")}</InputLabel>
            <div className={"relative"}>
              <select
                name="country"
                value={getCountryValue()}
                onChange={handleChange}
                required
                onInvalid={(e) => {
                  e.target.setCustomValidity(t("validation.selectCountry"));
                }}
                onInput={(e) => {
                  e.target.setCustomValidity("");
                }}
                className="appearance-none w-full h-12 px-3 py-2 text-lg text-text-medium border border-gray-200 rounded-lg focus:border-primary focus:outline-none"
              >
                <option value="">{t("labels.selectCountry")}</option>
                {countryOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {t(`options.${opt.name}`)}
                  </option>
                ))}
              </select>
              <Arrow />
            </div>
            {error.errors?.[0]?.country && (
              <p className="absolute top-20 text-red-500 text-[12px] mt-1">
                {error.errors[0].country}.
              </p>
            )}
          </div>
          {/* City */}
          <div className="mb-4 relative">
            <InputLabel>{t("labels.city")}</InputLabel>
            <div className={"relative"}>
              <MultiSelectDropdown
                options={cities.map((city) => ({
                  value: city.id,
                  label: city.name,
                }))}
                value={getSelectedCityIds()}
                onChange={handleCitiesChange}
                placeholder={t("labels.selectCity")}
                disabled={!formData.country}
              />
            </div>
            {error.errors?.[0]?.city && (
              <p className="absolute top-20 text-red-500 text-[12px] mt-1">
                {error.errors[0].city}.
              </p>
            )}
          </div>
          {/* Shipping Price per selected city */}
          {getSelectedCityIds().map((cityId) => {
            const cityOption = cities.find(
              (city) => Number(city.id) === Number(cityId)
            );
            const cityName = cityOption?.name || cityId;
            const shippingPriceValue =
              formData.shipping_prices?.[cityId] ??
              formData.shipping_prices?.[String(cityId)] ??
              "";

            return (
              <div key={cityId} className="mb-4 relative">
                <InputLabel>
                  {t("labels.shippingPriceForCity", { city: cityName })}
                </InputLabel>
                <Input
                  type="text"
                  name={`shipping_price_${cityId}`}
                  value={shippingPriceValue}
                  onChange={(e) =>
                    handleCityShippingPriceChange(cityId, e.target.value)
                  }
                  lang={i18n.language}
                  onInput={(e) => {
                    const value = e.target.value;
                    if (value && !/^\d*\.?\d*$/.test(value)) {
                      e.target.setCustomValidity(
                        t("validation.shippingPriceValidNumber")
                      );
                    } else {
                      e.target.setCustomValidity("");
                    }
                  }}
                  placeholder={t("placeholders.shippingPrice")}
                  required
                />
                {error.errors?.shipping_prices && (
                  <p className="absolute top-20 text-red-500 text-[12px] mt-1">
                    {error.errors.shipping_prices}
                  </p>
                )}
              </div>
            );
          })}

          {/* Supported Email */}
          <div className="mb-4 relative">
            <InputLabel>{t("labels.email")}</InputLabel>
            <Input
              type="text"
              name="supported_email"
              value={formData.supported_email}
              onChange={handleChange}
              placeholder={t("placeholders.email")}
              lang={i18n.language}
              onInvalid={(e) => {
                e.target.setCustomValidity(t("validation.selectValidEmail"));
              }}
              onInput={(e) => {
                e.target.setCustomValidity("");
              }}
              required
            />
            {!/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(
              formData.supported_email
            ) &&
              formData.supported_email && (
                <p className="absolute top-20 text-red-500 text-[12px] mt-1">
                  {t("validation.emailInvalid")}
                </p>
              )}
          </div>

          {/* Website */}
          <div className="mb-4 relative">
            <label className="block text-text-medium text-[16px] font-[600] mb-2">
              {t("labels.website")}
            </label>
            <Input
              type="text"
              name="website"
              value={
                formData.website && formData.website.toLowerCase() !== "null"
                  ? formData.website
                  : ""
              }
              onChange={handleChange}
              placeholder={t("placeholders.website")}
              // Remove required attribute
            />
            {/* Only show validation error if website is not empty and invalid */}
            {formData.website &&
              formData.website.trim() !== "" &&
              formData.website.toLowerCase() !== "null" &&
              !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/.test(
                formData.website
              ) && (
                <p className="absolute top-20 text-red-500 text-[12px] mt-1">
                  {t("validation.websiteInvalid")}
                </p>
              )}
          </div>
        </div>

        <div className="flex-1 h-full">
          {/* Portfolio file upload */}
          <div className="mb-4 relative">
            <InputLabel>{t("labels.portfolio")}</InputLabel>
            <div
              onDragOver={handlePortfolioDragOver}
              onDragLeave={handlePortfolioDragLeave}
              onDrop={handlePortfolioDrop}
              className={`relative border-2 border-dashed rounded-lg p-1 text-center transition-all ${
                isPortfolioDragging
                  ? "border-purple-500 bg-purple-50"
                  : "border-gray-300 bg-white"
              }`}
            >
              <input
                id="portfolioInput"
                type="file"
                name="portfolio"
                onChange={handleChange}
                className="hidden"
              />

              <div className="flex items-center justify-between">
                <p className="text-gray-500 text-base">
                  {formData.portfolio ? (
                    <span className="text-gray-700 font-medium">
                      {formData.portfolio.name}
                    </span>
                  ) : formData.portfolio_url ? (
                    <span className="text-gray-700 font-medium">
                      {t("labels.existingPortfolio")}
                    </span>
                  ) : (
                    t("placeholders.portfolioDragDrop")
                  )}
                </p>

                <div className="flex gap-2">
                  {formData.portfolio_url && !formData.portfolio && (
                    <a
                      href={formData.portfolio_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors text-sm"
                    >
                      {t("buttons.viewCurrent")}
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={handlePortfolioBrowseClick}
                    className="px-6 py-2 bg-[#5007C5] text-white rounded-lg font-medium hover:bg-[#6B5B9A] transition-colors"
                  >
                    {formData.portfolio_url && !formData.portfolio 
                      ? t("placeholders.portfolioReplace") 
                      : t("placeholders.portfolioUpload")
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* About */}
          <div className="mb-4 relative">
            <InputLabel>{t("labels.about")}</InputLabel>
            <textarea
              name="about"
              value={formData.about}
              onChange={handleChange}
              rows={9}
              maxLength={1000}
              required
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
              onInvalid={(e) => {
                e.target.setCustomValidity(t("validation.aboutMinimum"));
              }}
              onInput={(e) => {
                e.target.setCustomValidity("");
              }}
              className="w-full h-full resize-none bg-transparent border bg-white border-gray-200 rounded-lg p-3 text-lg text-text-medium font-Inter focus:border-primary focus:outline-none"
            />
            <div className="absolute bottom-5 right-5 text-text-medium text-[16px]">
              {1000 - (formData?.about?.length || 0)}
            </div>
            {formData?.about?.length < 10 && formData?.about && (
              <p className="absolute bottom-[-10px] text-red-500 text-[12px] mt-1">
                {t("validation.aboutLength")}
              </p>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className="mb-4 relative">
            <InputLabel>{t("labels.termsAndConditions")}</InputLabel>
            <textarea
              name="terms_and_conditions"
              value={formData.terms_and_conditions}
              onChange={handleChange}
              rows={9}
              maxLength={1000}
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
              onInvalid={(e) => {
                e.target.setCustomValidity(t("validation.termsMinimum"));
              }}
              onInput={(e) => {
                e.target.setCustomValidity("");
              }}
              required
              className="w-full h-full resize-none bg-transparent border bg-white border-gray-200 rounded-lg p-3 text-lg text-text-medium font-Inter focus:border-primary focus:outline-none"
            />
            <div className="absolute bottom-5 right-5 text-text-medium text-[16px]">
              {1000 - (formData?.terms_and_conditions?.length || 0)}
            </div>
            {formData?.terms_and_conditions?.length < 10 &&
              formData?.terms_and_conditions && (
                <p className="absolute bottom-[-10px] text-red-500 text-[12px] mt-1">
                  {t("validation.termsLength")}
                </p>
              )}
          </div>

          {/* Buttons */}
          <div className="flex justify-between gap-3 mt-4">
            <button
              type="button"
              className="flex-1 bg-purple-50 text-primary border border-gray-200 rounded-lg font-semibold py-3 px-2 transition duration-300"
              onClick={() => {
                sessionStorage.clear();
                navigate("/suppliers", { replace: true });
              }}
            >
              {t("buttons.cancel")}
            </button>
            <button
              type="submit"
              className={`flex-1 ${
                valid ? "bg-[#6609F8]" : "bg-[#888888]"
              } text-white rounded-lg font-semibold py-3 px-2 transition duration-300`}
            >
              {id ? t("buttons.submitEdit") : t("buttons.submitAdd")}
            </button>
          </div>
        </div>
      </form>

      {/* Focus selector modal */}
      {focusOverlay.show && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pt-[100px]">
          <div className="bg-white rounded-2xl p-6 max-w-7xl max-h-[90vh] w-full overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("focus.setFocus")}
              </h3>
              <button
                onClick={closeFocusSelector}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="18"
                    y1="6"
                    x2="6"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {t("focus.dragInstruction")}
            </p>

            {/* Focus selector area */}
            <div className="mb-6">
              <div
                ref={overlayRef}
                className={`relative mx-auto border-2 border-gray-300 rounded-2xl overflow-hidden shadow-lg select-none ${
                  isDragging ? "cursor-grabbing" : "cursor-grab"
                }`}
                style={{
                  width: Math.min(TARGET_WIDTH, window.innerWidth - 100),
                  height: Math.min(
                    TARGET_HEIGHT,
                    (window.innerWidth - 100) / TARGET_ASPECT_RATIO
                  ),
                  maxWidth: "100%",
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
              >
                <img
                  src={formData.preview}
                  alt="Focus selection"
                  className="w-full h-full object-cover pointer-events-none"
                  style={{
                    objectPosition: `${focusPoint.x}% ${focusPoint.y}%`,
                  }}
                  draggable={false}
                />

                {/* Focus point indicator */}
                <div
                  className={`absolute w-6 h-6 bg-blue-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 border-4 border-white shadow-lg pointer-events-none transition-all duration-150 ${
                    isDragging ? "scale-125 animate-pulse" : ""
                  }`}
                  style={{
                    left: `${focusPoint.x}%`,
                    top: `${focusPoint.y}%`,
                  }}
                />

                {/* Crosshairs */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: `${focusPoint.x}%`,
                    top: `${focusPoint.y}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div
                    className={`h-0.5 bg-white shadow-md transition-all duration-150 ${
                      isDragging ? "w-12" : "w-8"
                    } -translate-x-1/2`}
                  ></div>
                  <div
                    className={`w-0.5 bg-white shadow-md transition-all duration-150 ${
                      isDragging ? "h-12" : "h-8"
                    } -translate-y-1/2 -translate-x-1/2`}
                  ></div>
                </div>

                {/* Overlay instructions */}
                {!isDragging && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200">
                    <div className="bg-black bg-opacity-60 text-white px-3 py-1 rounded-lg text-sm opacity-0 hover:opacity-100 transition-opacity duration-200">
                      {t("focus.dragClick")}
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center mt-2 text-sm text-gray-500">
                {t("focus.point")}: {Math.round(focusPoint.x)}%,{" "}
                {Math.round(focusPoint.y)}%
                {isDragging && (
                  <span className="ml-2 text-blue-500 font-medium">
                    {t("focus.dragging")}...
                  </span>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={closeFocusSelector}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                {t("focus.cancel")}
              </button>
              <button
                onClick={saveFocusPoint}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
              >
                {t("focus.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InputForm;
